import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../layouts/AdminLayout';
import { FaArrowLeft, FaTrash, FaSave, FaUser, FaBuilding, FaEnvelope, FaPhone, FaTag, FaCalendarAlt, FaStickyNote, FaMapMarkerAlt, FaBriefcase, FaDollarSign, FaTasks, FaComments, FaUserCheck, FaClock, FaMobileAlt, FaBirthdayCake, FaBell } from 'react-icons/fa';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const CATEGORIES = [
  'abnormal_rigging', 'container', 'minerals', 'general', 'uncategorized',
  'parkhomes', 'agriculture', 'flatbeds', 'bulkloads', 'earth_moving',
  'explosives', 'generators', 'ground_engaging_tools', 'mining_rigging',
  'pipes', 'tanks', 'transformers', 'solar', 'electra_mining'
];

const STATUS_OPTIONS = [
  { value: '', label: 'No Status', pipeline: 'onboarding', stage: 'not_started' },
  { value: 'yellow', label: '🟡 Onboarding', pipeline: 'onboarding', stage: 'not_started' },
  { value: 'blue', label: '🔵 In Deals', pipeline: 'deals', stage: 'new' },
  { value: 'green', label: '🟢 Completed / Won', pipeline: 'deals', stage: 'won' },
  { value: 'red', label: '🔴 Closed - Not Interested', pipeline: 'deals', stage: 'lost' },
  { value: 'orange', label: '🟠 On Hold', pipeline: 'deals', stage: 'on_hold' },
];

const STRIP_FIELDS_ON_SAVE = ['notes', 'activities', 'id', 'created_at', 'updated_at'];

export default function CRMRecordDetail({ onLogout }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [record, setRecord] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [notes, setNotes] = useState([]);

  // ── Confirm modal state ──
  const [confirmModal, setConfirmModal] = useState(null); // { title, message, onConfirm }

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const adminUsers = users.filter(u => u.role === 'admin' || u.role === 'super_admin');

  const apiCall = useCallback(async (url, options = {}) => {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    const t = localStorage.getItem('token');
    const res = await fetch(`${API_BASE}${url}`, {
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${t}`, 'X-User-Id': u.id, 'X-User-Role': u.role },
      ...options
    });
    if (res.headers.get('content-type')?.includes('application/json')) return res.json();
    return res;
  }, []);

  useEffect(() => {
    if (id) {
      setLoading(true);
      apiCall(`/crm/records/${id}`).then(data => {
        if (data.success) {
          const rec = data.data;
          if (rec.record_type === 'contact' && !rec.address && rec.company_id) {
            apiCall(`/crm/records/${rec.company_id}`).then(companyRes => {
              if (companyRes.success && companyRes.data && companyRes.data.address) {
                setRecord(prev => (prev ? { ...prev, address: prev.address || companyRes.data.address } : rec));
              }
            });
          }
          setRecord(rec);
          setNotes(rec.notes || []);
        }
        setLoading(false);
      }).catch(() => setLoading(false));
    }
  }, [id]);

  useEffect(() => {
    apiCall('/users').then(data => {
      if (data.success) setUsers(data.users || data.data || []);
    });
  }, []);

  // ── Confirm actions ──
  const confirmDelete = () => {
    setConfirmModal({
      title: 'Delete Record',
      message: 'Are you sure you want to delete this record permanently? This action cannot be undone.',
      onConfirm: async () => {
        await apiCall(`/crm/records/${id}`, { method: 'DELETE' });
        navigate(-1);
      }
    });
  };

  const confirmDeleteNote = (noteId) => {
    setConfirmModal({
      title: 'Delete Note',
      message: 'Are you sure you want to delete this note?',
      onConfirm: async () => {
        const res = await apiCall(`/crm/notes/${noteId}`, { method: 'DELETE' });
        if (res.success) {
          setNotes(prev => prev.filter(n => n.id !== noteId));
        }
      }
    });
  };

  const handleSave = async () => {
    setSaving(true);
    const payload = { ...record };
    STRIP_FIELDS_ON_SAVE.forEach(field => delete payload[field]);
    const res = await apiCall(`/crm/records/${id}`, { method: 'PATCH', body: JSON.stringify(payload) });
    if (res.success) {
      toast.success('Saved');
    } else {
      toast.error('Save failed: ' + (res.error || 'Unknown error'));
    }
    setSaving(false);
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    const res = await apiCall('/crm/notes', { method: 'POST', body: JSON.stringify({ record_id: id, note: newNote, created_by: u.id }) });
    if (res.success) {
      setNotes(prev => [res.data, ...prev]);
      setNewNote('');
    }
  };

  const handleAssign = async (ownerId) => {
    const update = ownerId
      ? { assigned_to: ownerId }
      : { assigned_to: null, color_status: null };
    const res = await apiCall(`/crm/records/${id}`, { method: 'PATCH', body: JSON.stringify(update) });
    if (res.success) setRecord(prev => ({ ...prev, ...update }));
  };

  const handleStatusChange = (e) => {
    const selected = STATUS_OPTIONS.find(s => s.value === e.target.value);
    setRecord({
      ...record,
      color_status: selected ? selected.value : null,
      pipeline: selected ? selected.pipeline : null,
      stage: selected ? selected.stage : null,
    });
  };

  if (loading) return (
    <AdminLayout activeTab="crm" onLogout={onLogout} user={user}>
      <div style={{ textAlign:'center', padding:60, background:'#fff', borderRadius:12 }}>🔄 Loading record...</div>
    </AdminLayout>
  );

  if (!record) return (
    <AdminLayout activeTab="crm" onLogout={onLogout} user={user}>
      <div style={{ textAlign:'center', padding:60, background:'#fff', borderRadius:12 }}>
        <div style={{ fontSize:48, marginBottom:12 }}>🔍</div>
        <h2 style={{ color:'#0A1929', margin:'0 0 8px' }}>Record Not Found</h2>
        <button onClick={() => navigate(-1)} style={{ padding:'8px 20px', background:'#0A1929', color:'#fff', border:'none', borderRadius:8, cursor:'pointer', marginTop:12 }}>← Go Back</button>
      </div>
    </AdminLayout>
  );

  return (
    <AdminLayout activeTab="crm" onLogout={onLogout} user={user}>
      <div style={{ maxWidth:900, margin:'0 auto' }}>
        <button onClick={() => navigate(-1)} style={{ border:'none', background:'none', cursor:'pointer', color:'#475569', fontSize:13, display:'flex', alignItems:'center', gap:6, marginBottom:16 }}>
          <FaArrowLeft /> Back
        </button>

        {/* ═══ MAIN CARD ═══ */}
        <div style={{ background:'#fff', borderRadius:12, padding:24, boxShadow:'0 1px 3px rgba(0,0,0,.06)', marginBottom:16 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20 }}>
            <div>
              <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:4 }}>
                <h2 style={{ margin:0, fontSize:20, fontWeight:700, color:'#0A1929' }}>
                  {record.record_type === 'company' ? record.company_name : (record.contact_name || 'Unnamed Contact')}
                </h2>
                {record.color_status && (
                  <span style={{
                    width:12, height:12, borderRadius:'50%', display:'inline-block',
                    background: record.color_status === 'yellow' ? '#f59e0b' : record.color_status === 'blue' ? '#2563eb' : record.color_status === 'green' ? '#16a34a' : record.color_status === 'red' ? '#dc2626' : record.color_status === 'orange' ? '#f97316' : '#94a3b8'
                  }}></span>
                )}
              </div>
              {record.record_type === 'contact' && record.company_name && (
                <p style={{ margin:'4px 0 0', color:'#6366f1', fontSize:14, fontWeight:500 }}><FaBuilding style={{ marginRight:6 }} />{record.company_name}</p>
              )}
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <button onClick={handleSave} disabled={saving} style={{ padding:'8px 16px', background:'#0A1929', color:'#fff', border:'none', borderRadius:8, cursor:'pointer', fontSize:12, fontWeight:600, display:'flex', alignItems:'center', gap:6 }}>
                <FaSave size={12} /> {saving ? 'Saving...' : 'Save'}
              </button>
              <button onClick={confirmDelete} style={{ padding:'8px 16px', background:'#fee2e2', color:'#dc2626', border:'none', borderRadius:8, cursor:'pointer', fontSize:12, fontWeight:600, display:'flex', alignItems:'center', gap:6 }}>
                <FaTrash size={12} /> Delete
              </button>
            </div>
          </div>

          {/* ═══ ALLOCATE / STATUS ROW ═══ */}
          <div style={{ display:'flex', gap:10, alignItems:'center', marginBottom:16, padding:'12px 16px', background:'#f8fafc', borderRadius:10, flexWrap:'wrap' }}>
            <FaUserCheck style={{ color:'#8b5cf6' }} />
            <span style={{ fontSize:12, color:'#475569', fontWeight:600 }}>Owner:</span>
            <select value={record.assigned_to || ''} onChange={e => handleAssign(e.target.value || null)} style={{ padding:'6px 12px', borderRadius:6, border:'1px solid #e2e8f0', fontSize:13, background:'#fff' }}>
              <option value="">Unassigned</option>
              {adminUsers.map(u => <option key={u.id} value={u.id}>{u.full_name || u.email}</option>)}
            </select>
            <div style={{ width:1, height:20, background:'#e2e8f0', margin:'0 4px' }}></div>
            <FaClock style={{ color:'#8b5cf6' }} />
            <span style={{ fontSize:12, color:'#475569', fontWeight:600 }}>Status:</span>
            <select value={record.color_status || ''} onChange={handleStatusChange} style={{ padding:'6px 12px', borderRadius:6, border:'1px solid #e2e8f0', fontSize:13, background:'#fff' }}>
              {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>

          {/* ═══ EDITABLE FIELDS ═══ */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
            {record.record_type === 'contact' && (
              <DetailField icon={<FaUser />} label="Contact Name" value={record.contact_name} onChange={v => setRecord({...record, contact_name: v})} />
            )}
            {record.record_type === 'company' && (
              <DetailField icon={<FaBuilding />} label="Company Name" value={record.company_name} onChange={v => setRecord({...record, company_name: v})} />
            )}
            <DetailField icon={<FaBriefcase />} label="Position / Title" value={record.position} onChange={v => setRecord({...record, position: v})} />
            <DetailField icon={<FaEnvelope />} label="Email" value={record.email} onChange={v => setRecord({...record, email: v})} />
            <DetailField icon={<FaPhone />} label="Phone" value={record.phone} onChange={v => setRecord({...record, phone: v})} />
            <DetailField icon={<FaMobileAlt />} label="Cell Phone" value={record.cell_phone} onChange={v => setRecord({...record, cell_phone: v})} />
            <DetailField icon={<FaDollarSign />} label="Monthly Avg Billings (R)" value={record.monthly_avg_billings} onChange={v => setRecord({...record, monthly_avg_billings: v})} type="number" />
            <DetailField icon={<FaBirthdayCake />} label="Birthday" value={record.birthday} onChange={v => setRecord({...record, birthday: v})} type="date" />
            <DetailField icon={<FaMapMarkerAlt />} label="Address" value={record.address} onChange={v => setRecord({...record, address: v})} full />
            <div style={{ gridColumn:'1/-1', display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
              <div>
                <label style={{ fontSize:11, fontWeight:600, color:'#64748b', textTransform:'uppercase', letterSpacing:'.5px', display:'flex', alignItems:'center', gap:6, marginBottom:4 }}>
                  <FaTag size={12} /> Category
                </label>
                <select value={record.category || 'uncategorized'} onChange={e => setRecord({...record, category: e.target.value})}
                  style={{ width:'100%', padding:'8px 12px', borderRadius:8, border:'1px solid #e2e8f0', fontSize:13, color:'#0A1929', background:'#f8fafc', outline:'none', boxSizing:'border-box' }}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c.replace(/_/g,' ').toUpperCase()}</option>)}
                </select>
              </div>
              <DetailField icon={<FaStickyNote />} label="City" value={record.city} onChange={v => setRecord({...record, city: v})} />
            </div>
            <DetailField icon={<FaBell />} label="Follow-up Date" value={record.reminder_date ? record.reminder_date.slice(0, 16) : ''} onChange={v => setRecord({...record, reminder_date: v ? new Date(v).toISOString() : null})} type="datetime-local" />
            <DetailField icon={<FaStickyNote />} label="Follow-up Note" value={record.reminder_note} onChange={v => setRecord({...record, reminder_note: v})} />
            <DetailField icon={<FaStickyNote />} label="Research Notes" value={record.research_notes} onChange={v => setRecord({...record, research_notes: v})} full />
            <DetailField icon={<FaTasks />} label="Next Action" value={record.next_action} onChange={v => setRecord({...record, next_action: v})} full />
          </div>
        </div>

        {/* ═══ NOTES / COMMENTS ═══ */}
        <div style={{ background:'#fff', borderRadius:12, padding:24, boxShadow:'0 1px 3px rgba(0,0,0,.06)' }}>
          <h3 style={{ margin:'0 0 12px', fontSize:15, fontWeight:700, color:'#0A1929', display:'flex', alignItems:'center', gap:8 }}>
            <FaComments style={{ color:'#8b5cf6' }} /> Notes & Comments
          </h3>
          <div style={{ display:'flex', gap:8, marginBottom:16 }}>
            <input type="text" placeholder="Add a note or comment..." value={newNote} onChange={e => setNewNote(e.target.value)} onKeyDown={e => { if(e.key==='Enter') addNote(); }}
              style={{ flex:1, padding:'8px 14px', borderRadius:8, border:'1px solid #e2e8f0', fontSize:13, outline:'none' }} />
            <button onClick={addNote} disabled={!newNote.trim()} style={{ padding:'8px 18px', background:'#0A1929', color:'#fff', border:'none', borderRadius:8, cursor:'pointer', fontWeight:600, fontSize:12, opacity: newNote.trim()?1:0.5 }}>
              Add
            </button>
          </div>
          {notes.length === 0 && <p style={{ color:'#94a3b8', fontSize:13 }}>No notes yet.</p>}
          {notes.map((n, i) => (
            <div key={n.id || i} className="crm-note-row" style={{ padding:'10px 14px', background: i%2===0?'#f8fafc':'#fff', borderRadius:8, marginBottom:6, fontSize:12, position:'relative' }}>
              <div style={{ color:'#334155', paddingRight:28 }}>{n.note}</div>
              <div style={{ color:'#94a3b8', fontSize:10, marginTop:4 }}>
                {n.created_by_name || (n.created_by ? 'User' : 'System')} • {new Date(n.created_at).toLocaleString()}
              </div>
              <button
                onClick={() => confirmDeleteNote(n.id)}
                title="Delete note"
                className="crm-note-delete"
                style={{
                  position:'absolute', top:8, right:10,
                  border:'none', background:'transparent', color:'#94a3b8', cursor:'pointer',
                  padding:'4px', borderRadius:4, fontSize:14, display:'flex', alignItems:'center', justifyContent:'center',
                  opacity:0, transition:'opacity 0.15s, color 0.15s'
                }}
                onMouseEnter={e => { e.currentTarget.style.color = '#dc2626'; }}
                onMouseLeave={e => { e.currentTarget.style.color = '#94a3b8'; }}
              >
                <FaTrash size={12} />
              </button>
            </div>
          ))}
        </div>

        {record.source_file && (
          <div style={{ marginTop:16, padding:'12px 16px', background:'#f8fafc', borderRadius:8, fontSize:11, color:'#64748b' }}>
            📄 Source: {record.source_file} / {record.source_sheet} (row {record.source_row})
          </div>
        )}

        {/* ── Inline CSS for hover effect ── */}
        <style>{`
          .crm-note-row:hover .crm-note-delete { opacity: 1 !important; }
          .crm-note-delete:hover { color: #dc2626 !important; }
        `}</style>
      </div>

      {/* ═══ CONFIRM MODAL ═══ */}
      {confirmModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }} onClick={() => setConfirmModal(null)}>
          <div style={{ background:'#fff', borderRadius:16, padding:28, maxWidth:420, width:'90%', boxShadow:'0 20px 60px rgba(0,0,0,.2)' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin:'0 0 8px', fontSize:18, fontWeight:700, color:'#0A1929' }}>{confirmModal.title}</h3>
            <p style={{ margin:'0 0 24px', fontSize:14, color:'#475569', lineHeight:1.5 }}>{confirmModal.message}</p>
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button
                onClick={() => setConfirmModal(null)}
                style={{ padding:'10px 22px', background:'#dc2626', color:'#fff', border:'none', borderRadius:8, cursor:'pointer', fontWeight:600, fontSize:13 }}
              >
                Cancel
              </button>
              <button
                onClick={() => { const fn = confirmModal.onConfirm; setConfirmModal(null); fn(); }}
                style={{ padding:'10px 22px', background:'#16a34a', color:'#fff', border:'none', borderRadius:8, cursor:'pointer', fontWeight:600, fontSize:13 }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

function DetailField({ icon, label, value, onChange, full, type }) {
  return (
    <div style={full ? { gridColumn:'1/-1' } : {}}>
      <label style={{ fontSize:11, fontWeight:600, color:'#64748b', textTransform:'uppercase', letterSpacing:'.5px', display:'flex', alignItems:'center', gap:6, marginBottom:4 }}>
        {icon} {label}
      </label>
      <input
        type={type || 'text'}
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        style={{ width:'100%', padding:'8px 12px', borderRadius:8, border:'1px solid #e2e8f0', fontSize:13, color:'#0A1929', background:'#f8fafc', outline:'none', boxSizing:'border-box' }}
        onFocus={e => e.currentTarget.style.borderColor='#6366f1'}
        onBlur={e => e.currentTarget.style.borderColor='#e2e8f0'}
      />
    </div>
  );
}